import { useState } from "react";
import {
  Button,
  MenuItem,
  OutlinedInput,
  Select,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  TextField,
  Autocomplete,
  Box,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import "./MarinerListComponent.scss";
import { useGetMarinersQuery } from "../../redux/api/mariner";
import {
  marinerSearchParams,
  MarinerProfile,
  MarinerStatus,
  MarinerStatusLabel,
} from "../../common/MarinerProfile";
import { jobRatingEnumToString } from "../../util/jobRating.util";
import { JobRating, JobRatingLabel } from "../../common/JobOrder";
import useDocumentTitle from "../../util/useDocumentTitle";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import { Vessel } from "../../common/Vessel";
import { marinerStatusText } from "../../common/CognitoUser/MarinerStatusText";

const MarinerListComponent = () => {
  useDocumentTitle("Mariners");
  const navigate = useNavigate();
  const redirectToProfile = () => {
    const newProfileId = "new";
    navigate(`/profiles/${newProfileId}`);
  };
  const user = useSelector(selectCurrentUser);
  const userGroup = user?.userGroup as UserGroup;
  const isViewOnly =
    userGroup === UserGroup.CREWING_EXECS ||
    userGroup === UserGroup.FLEET_EXECS ||
    userGroup === UserGroup.VESSEL_EXECS;
  const noAddAccess =
    userGroup === UserGroup.CREWING_EXECS ||
    userGroup === UserGroup.HUMAN_RESOURCES ||
    userGroup === UserGroup.FLEET_EXECS ||
    userGroup === UserGroup.VESSEL_EXECS;
  const hasVeteranAccess =
    userGroup === UserGroup.HUMAN_RESOURCES ||
    userGroup === UserGroup.HUMAN_RESOURCES_ADMINS;
  const isMariner = userGroup === UserGroup.MARINERS;
  const [vessel, setVessel] = useState<Vessel | null>(null);

  const [searchParams, setSearchParams] = useState<marinerSearchParams>({
    search: "",
    ratings: [],
    vesselId: undefined,
    registered: undefined,
    compatibleWithRating: "",
    smis: "",
    veteran: undefined,
    trainingStatus: "",
    page: 0,
    size: 15,
    sortColumn: "firstName",
    sortDirection: "asc",
  });

  const {
    data: marinerProfiles,
    isLoading,
    isFetching,
  } = useGetMarinersQuery(searchParams);
  const { data: vessels, isLoading: isVesselLoading } = useGetVesselsQuery({});

  const handleSort =
    (property: keyof MarinerProfile) => (event: React.MouseEvent<unknown>) => {
      const direction =
        searchParams.sortColumn === property &&
        searchParams.sortDirection === "asc"
          ? "desc"
          : "asc";
      setSearchParams({
        ...searchParams,
        sortColumn: property,
        sortDirection: direction,
      });
    };

  const handleChange = (event: { target: { value: any } }) => {
    const {
      target: { value },
    } = event;
    setSearchParams({
      ...searchParams,
      // On autofill we get a stringified value.
      ratings: typeof value === "string" ? value.split(",") : value,
      page: 0,
    });
  };

  if (isVesselLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <h1>Mariners</h1>
      <div className="filters-wrap">
        <div className="filters-list">
          <label>
            <span>Search</span>
            <TextField
              id="search"
              onChange={(event) => {
                setSearchParams({
                  ...searchParams,
                  search: event.target.value,
                  page: 0,
                });
              }}
              variant="outlined"
              placeholder="Name or MMC ID"
              size="small"
            />
          </label>
          <label>
            <span>
              Ratings<span className="info-label">(Multi-Select)</span>
            </span>
            <Select
              id="rating"
              multiple
              displayEmpty
              value={searchParams?.ratings}
              onChange={handleChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <div className="react-select">Ratings</div>;
                }

                return selected.join(", ");
              }}
            >
              {jobRatingEnumToString().map((rating) => (
                <MenuItem key={rating} value={rating.replaceAll("_", "")}>
                  {JobRatingLabel[JobRating[rating as keyof typeof JobRating]]}
                </MenuItem>
              ))}
            </Select>
          </label>
          <label className="large-filter">
            <span>Vessel:</span>
            <Autocomplete
              options={
                vessels ? (vessels as Vessel[]).filter((v) => !v.disabled) : []
              }
              value={vessel}
              onChange={(event, value) => {
                setVessel(value);
                setSearchParams({
                  ...searchParams,
                  vesselId: value?.id,
                  page: 0,
                });
              }}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => (opt.name ? opt.name : "")}
            />
          </label>

          {!isMariner && (
            <>
              <label>
                <span>Registration Status</span>
                {/* use empty string as the null value to avoid an uncontrolled component */}
                <Select
                  id="registrationStatus"
                  displayEmpty={true}
                  value={
                    searchParams?.registered === undefined
                      ? ""
                      : searchParams.registered.toString()
                  }
                  onChange={(event) =>
                    setSearchParams({
                      ...searchParams,
                      registered:
                        event.target.value === ""
                          ? undefined
                          : event.target.value === "true",
                      page: 0,
                    })
                  }
                  input={<OutlinedInput />}
                >
                  <MenuItem value={""}></MenuItem>
                  <MenuItem value={"true"}>Registered</MenuItem>
                  <MenuItem value={"false"}>Not Registered</MenuItem>
                </Select>
              </label>
              <label>
                <span>SMIS #</span>
                <TextField
                  id="search"
                  onChange={(event) => {
                    setSearchParams({
                      ...searchParams,
                      smis: event.target.value,
                      page: 0,
                    });
                  }}
                  variant="outlined"
                  placeholder="SMIS #"
                  size="small"
                />
              </label>
              {hasVeteranAccess && (
                <label>
                  <span>Veteran</span>
                  <Select
                    id="veteranStatus"
                    displayEmpty={true}
                    value={
                      searchParams?.veteran === undefined
                        ? ""
                        : searchParams.veteran.toString()
                    }
                    onChange={(event) =>
                      setSearchParams({
                        ...searchParams,
                        veteran:
                          event.target.value === ""
                            ? undefined
                            : event.target.value === "true",
                        page: 0,
                      })
                    }
                    input={<OutlinedInput />}
                  >
                    <MenuItem value={""}></MenuItem>
                    <MenuItem value={"true"}>Veteran</MenuItem>
                    <MenuItem value={"false"}>Not Veteran</MenuItem>
                  </Select>
                </label>
              )}
              <label>
                <span>Training Status</span>
                <Select
                  id="trainingStatus"
                  displayEmpty={true}
                  value={
                    searchParams?.trainingStatus === ""
                      ? ""
                      : searchParams.trainingStatus.toString()
                  }
                  onChange={(event) =>
                    setSearchParams({
                      ...searchParams,
                      trainingStatus: event.target.value,
                      page: 0,
                    })
                  }
                  input={<OutlinedInput />}
                >
                  <MenuItem value={""}></MenuItem>
                  <MenuItem value={"PERMANENT"}>Permanent</MenuItem>
                  <MenuItem value={"ACTIVE"}>Active</MenuItem>
                  <MenuItem value={"INACTIVE"}>Inactive</MenuItem>
                </Select>
              </label>
            </>
          )}
        </div>
        {!noAddAccess && (
          <Button
            variant="contained"
            className="blue-button"
            onClick={redirectToProfile}
          >
            New Mariner
          </Button>
        )}
      </div>
      <TableContainer>
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell className="rating">
                <TableSortLabel
                  active={searchParams.sortColumn === "firstName"}
                  direction={
                    searchParams.sortColumn === "firstName"
                      ? searchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("firstName")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={searchParams.sortColumn === "mmcId"}
                  direction={
                    searchParams.sortColumn === "mmcId"
                      ? searchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("mmcId")}
                >
                  MMC ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={searchParams.sortColumn === "rating"}
                  direction={
                    searchParams.sortColumn === "rating"
                      ? searchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("rating")}
                >
                  Rating
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={searchParams.sortColumn === "type"}
                  direction={
                    searchParams.sortColumn === "type"
                      ? searchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("type")}
                >
                  Rating Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={searchParams.sortColumn === "status"}
                  direction={
                    searchParams.sortColumn === "status"
                      ? searchParams.sortDirection
                      : "asc"
                  }
                  onClick={handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {!isMariner && <TableCell>Registration Status</TableCell>}
              {/* Blank table cell to ensure full width table header */}
              {!isViewOnly && <TableCell></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading &&
              !isFetching &&
              marinerProfiles?.content &&
              marinerProfiles.content.map((row, index) => (
                <TableRow key={"mariner" + index}>
                  <TableCell className="rating">
                    {row.firstName} {row.middleName} {row.lastName}
                  </TableCell>
                  <TableCell>{row.mmcId}</TableCell>
                  <TableCell>
                    {
                      JobRatingLabel[
                        JobRating[row.rating as keyof typeof JobRating]
                      ]
                    }
                  </TableCell>
                  <TableCell>
                    {row.type} - {row.agentUnion}
                  </TableCell>
                  <TableCell>
                    {MarinerStatusLabel[row.status as MarinerStatus]}
                  </TableCell>
                  {!isMariner && (
                    <TableCell>
                      {marinerStatusText(
                        row?.cognitoUser?.status === undefined
                          ? ""
                          : row.cognitoUser.status
                      )}
                    </TableCell>
                  )}
                  {!isViewOnly && (
                    <TableCell>
                      <Link className="blue-link" to={`/profiles/${row.id}`}>
                        View
                      </Link>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component={"div"}
          count={(marinerProfiles?.totalElements as number) || 0}
          onPageChange={(event, newPage) =>
            setSearchParams({
              ...searchParams,
              page: newPage,
            })
          }
          rowsPerPageOptions={[15, 25, 50, 100]}
          page={searchParams?.page}
          rowsPerPage={searchParams?.size}
          onRowsPerPageChange={(event) =>
            setSearchParams({
              ...searchParams,
              size: parseInt(event.target.value),
              page: 0,
            })
          }
        />
      </TableContainer>
    </>
  );
};

export default MarinerListComponent;
